// Reads an Import-users CSV into per-row facts the preview table renders and
// the import POST sends. One header row, one person per row:
//
//   row 1   column names (the template's headers — matched by name, not order)
//   row 2+  the people
//
// papaparse does the reading, same as the data source importer and for the same
// reasons (quoted commas, CRLF, BOM). Validation is TWO-TIERED, unlike the
// datasource parser's all-or-nothing rule set:
//
//   file-level  → the file is refused outright (not a CSV, too large, headers
//                 missing) and the dropzone stays as it was;
//   row-level   → the row is kept, carrying its errors, so the preview can show
//                 it flagged while the valid rows import around it.
//
// That split is the product decision for this importer: "3 of 5 rows will be
// added", never "your file was rejected because row 4 is bad".
//
// The header contract descends from the legacy importer (git bc2fa128,
// `importUserFileHeaders`: First/Last Name, Country Code, Phone Number required,
// Personal ID optional) extended with the rewrite's optional profile fields.

import Papa from 'papaparse';
import { countries } from '@/mocks/countries';
import type { CreatePersonInput } from '@/api/endpoints/peopleApi';
import { todayPlus } from '@/utils/date';
import { EMPLOYMENT_TYPES, NATIONALITIES } from '../shared/personFields';

/** Reject anything larger up front — the parse is synchronous. */
export const MAX_PEOPLE_CSV_BYTES = 5 * 1024 * 1024;

/** One POST carries every ready row, so the file size is the payload size —
 *  capped well past any real roster upload but short of a mistaken export. */
export const MAX_PEOPLE_ROWS = 1000;

/** The value keys a row carries — the CreatePersonInput field each column
 *  fills, which is what keeps the CSV contract and the wire contract aligned. */
export type PeopleCsvKey =
  | 'firstName'
  | 'lastName'
  | 'countryCode'
  | 'mobileNumber'
  | 'email'
  | 'department'
  | 'employmentType'
  | 'personalIdNumber'
  | 'personalIdExpiry'
  | 'birthDate'
  | 'nationality';

export interface PeopleCsvColumn {
  /** The literal header cell — DATA (written into files), never translated. */
  label: string;
  key: PeopleCsvKey;
  required: boolean;
}

/** The template's columns, in template order. Header matching is by name
 *  (trimmed, case-insensitive), so a reordered file still imports. */
export const PEOPLE_CSV_COLUMNS: PeopleCsvColumn[] = [
  { label: 'First Name', key: 'firstName', required: true },
  { label: 'Last Name', key: 'lastName', required: true },
  { label: 'Country Code', key: 'countryCode', required: true },
  { label: 'Phone Number', key: 'mobileNumber', required: true },
  { label: 'Email', key: 'email', required: false },
  { label: 'Department', key: 'department', required: false },
  { label: 'Employment Type', key: 'employmentType', required: false },
  { label: 'ID Number', key: 'personalIdNumber', required: false },
  { label: 'ID Expiry', key: 'personalIdExpiry', required: false },
  { label: 'Birth Date', key: 'birthDate', required: false },
  { label: 'Nationality', key: 'nationality', required: false },
];

/** Every way the whole FILE can be refused, as i18n keys. */
export type PeopleCsvErrorKey =
  | 'people_import_error_not_csv'
  | 'people_import_error_too_large'
  | 'people_import_error_unreadable'
  | 'people_import_error_no_rows'
  | 'people_import_error_missing_column'
  | 'people_import_error_too_many_rows';

export interface PeopleCsvFailure {
  errorKey: PeopleCsvErrorKey;
  params?: Record<string, string | number>;
}

/** Every way one CELL can fail, as i18n keys. Each message interpolates
 *  {{column}} — the literal header label, since that is what the user must fix
 *  in their file. */
export type PeopleRowErrorKey =
  | 'people_import_err_required'
  | 'people_import_err_country_code'
  | 'people_import_err_phone'
  | 'people_import_err_email'
  | 'people_import_err_date'
  | 'people_import_err_birthdate'
  | 'people_import_err_option';

export interface PeopleRowError {
  key: PeopleCsvKey;
  errorKey: PeopleRowErrorKey;
  /** The literal column label, ready to interpolate. */
  column: string;
}

/** One parsed person. Values are trimmed and normalized (dial code with its
 *  `+`, canonical list casing); `errors` empty means the row is ready. */
export interface ImportPersonRow {
  /** 1-based file line (the header is line 1), readable off a spreadsheet. */
  line: number;
  values: Record<PeopleCsvKey, string>;
  errors: PeopleRowError[];
}

export interface ParsedPeopleCsv {
  rows: ImportPersonRow[];
  readyCount: number;
}

/** STRING discriminant, not a boolean — this project compiles with
 *  `strict: false`, where TypeScript will not narrow a union on a boolean. */
export type PeopleParseResult =
  | { status: 'ok'; parsed: ParsedPeopleCsv }
  | ({ status: 'error' } & PeopleCsvFailure);

const isCsv = (file: File) => file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';

const normalizeHeader = (cell: string) => cell.trim().toLowerCase();

/** Basic shape check only — the server owns real address validation. */
const emailRegex = /^\S+@\S+\.\S+$/;

/** Known dial codes, keyed without the `+` so `966` and `+966` both resolve. */
const dialByDigits = new Map(countries.map((c) => [c.dial.replace('+', ''), c.dial]));

/** Case-insensitive membership in a static option list, returning the CANONICAL
 *  value — `full-time` imports as `Full-time`, the exact string the backend and
 *  the filter arrays already use. */
const canonicalOption = (list: string[], value: string): string | null =>
  list.find((option) => option.toLowerCase() === value.toLowerCase()) ?? null;

/** Same acceptance rule as the datasource importer's dateTime cells: anything
 *  Date can parse — which is exactly what the payload's toISOString needs. */
const isParseableDate = (value: string) => !isNaN(new Date(value).getTime());

/**
 * Validate one row's cells IN PLACE, returning normalized values + errors.
 * A required-but-blank cell reports only the missing error, not a format one —
 * telling the user an empty phone is "not digits" would be noise.
 */
const validateRow = (
  line: number,
  cellOf: (key: PeopleCsvKey) => string,
): ImportPersonRow => {
  const values = {} as Record<PeopleCsvKey, string>;
  const errors: PeopleRowError[] = [];
  const fail = (column: PeopleCsvColumn, errorKey: PeopleRowErrorKey) =>
    errors.push({ key: column.key, errorKey, column: column.label });

  for (const column of PEOPLE_CSV_COLUMNS) {
    const raw = cellOf(column.key).trim();
    values[column.key] = raw;

    if (!raw) {
      if (column.required) fail(column, 'people_import_err_required');
      continue;
    }

    switch (column.key) {
      case 'countryCode': {
        const dial = dialByDigits.get(raw.replace(/^\+/, ''));
        if (dial) values.countryCode = dial;
        else fail(column, 'people_import_err_country_code');
        break;
      }
      case 'mobileNumber': {
        // Spaces/hyphens are formatting, not data — strip before judging.
        // National digits only, 7–12 of them (user-specified bounds).
        const digits = raw.replace(/[\s-]/g, '');
        if (/^\d{7,12}$/.test(digits)) values.mobileNumber = digits;
        else fail(column, 'people_import_err_phone');
        break;
      }
      case 'email':
        if (!emailRegex.test(raw)) fail(column, 'people_import_err_email');
        break;
      case 'personalIdExpiry':
        if (!isParseableDate(raw)) fail(column, 'people_import_err_date');
        break;
      case 'birthDate':
        if (!isParseableDate(raw)) fail(column, 'people_import_err_date');
        // Past-only, judged on the same instant the payload's toIso sends.
        else if (new Date(raw).toISOString().slice(0, 10) > todayPlus(-1))
          fail(column, 'people_import_err_birthdate');
        break;
      case 'employmentType': {
        const canonical = canonicalOption(EMPLOYMENT_TYPES, raw);
        if (canonical) values.employmentType = canonical;
        else fail(column, 'people_import_err_option');
        break;
      }
      case 'nationality': {
        const canonical = canonicalOption(NATIONALITIES, raw);
        if (canonical) values.nationality = canonical;
        else fail(column, 'people_import_err_option');
        break;
      }
      default:
        break;
    }
  }

  return { line, values, errors };
};

/**
 * Validate the whole file from the RAW row matrix. Pure and File-free so it can
 * be exercised without the DOM. Returns a file-level failure, or every data row
 * — flagged ones included — for the preview to render.
 */
export const validatePeopleCsv = (rows: string[][]): PeopleParseResult => {
  if (rows.length < 2) return { status: 'error', errorKey: 'people_import_error_no_rows' };
  if (rows.length - 1 > MAX_PEOPLE_ROWS)
    return {
      status: 'error',
      errorKey: 'people_import_error_too_many_rows',
      params: { max: MAX_PEOPLE_ROWS },
    };

  // Order-independent header map (the legacy importer's rule): each known
  // column's position by its normalized name; unknown extras are ignored.
  const header = rows[0].map(normalizeHeader);
  const indexOf = new Map<PeopleCsvKey, number>();
  for (const column of PEOPLE_CSV_COLUMNS) {
    const index = header.indexOf(normalizeHeader(column.label));
    if (index !== -1) indexOf.set(column.key, index);
  }

  const missing = PEOPLE_CSV_COLUMNS.find((c) => c.required && !indexOf.has(c.key));
  if (missing)
    return {
      status: 'error',
      errorKey: 'people_import_error_missing_column',
      params: { name: missing.label },
    };

  const parsed = rows.slice(1).map((row, index) =>
    validateRow(index + 2, (key) => {
      const at = indexOf.get(key);
      return at === undefined ? '' : (row[at] ?? '');
    }),
  );

  return {
    status: 'ok',
    parsed: {
      rows: parsed,
      readyCount: parsed.filter((row) => row.errors.length === 0).length,
    },
  };
};

/** Read a picked file into rows, or the reason not to. Never throws — every
 *  failure comes back as `{ status: 'error' }`. */
export const parsePeopleCsv = (file: File): Promise<PeopleParseResult> =>
  new Promise((resolve) => {
    if (!isCsv(file)) return resolve({ status: 'error', errorKey: 'people_import_error_not_csv' });
    if (file.size > MAX_PEOPLE_CSV_BYTES)
      return resolve({ status: 'error', errorKey: 'people_import_error_too_large' });

    Papa.parse<string[]>(file, {
      // 'greedy' drops all-blank lines (`,,,,`); a row with any real value
      // survives and its blank optional cells stay legal.
      skipEmptyLines: 'greedy',
      complete: ({ data }) =>
        resolve(validatePeopleCsv(data.filter((row) => row.length > 0))),
      error: () => resolve({ status: 'error', errorKey: 'people_import_error_unreadable' }),
    });
  });

/** yyyy-mm-dd (or anything Date parses — see isParseableDate) → ISO datetime. */
const toIso = (d: string) => (d ? new Date(d).toISOString() : undefined);

/**
 * One READY row → the wire shape. Lives beside the parser so the CSV columns
 * and the payload fields cannot drift apart. `roles` is the row's effective
 * selection (per-row override or the imported-people default); structure and
 * tags are empty by product decision — placement happens after import.
 */
export const rowToCreatePersonInput = (
  row: ImportPersonRow,
  roleIds: string[],
): CreatePersonInput => ({
  firstName: row.values.firstName,
  lastName: row.values.lastName,
  countryCode: row.values.countryCode,
  mobileNumber: row.values.mobileNumber,
  email: row.values.email || undefined,
  roles: roleIds,
  companyStructureNodes: [],
  tags: [],
  personalIdNumber: row.values.personalIdNumber || undefined,
  personalIdExpiry: toIso(row.values.personalIdExpiry),
  nationality: row.values.nationality || undefined,
  birthDate: toIso(row.values.birthDate),
  department: row.values.department || undefined,
  employmentType: row.values.employmentType || undefined,
});
